import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { styled } from "@mui/material";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { animate } from "animejs";
import React from "react";
import { Solver } from "./Solver";

const STextField = styled(TextField)(() => ({
  textAlign: "center",
  overflow: "hidden",
  width: "45px",
  padding: 0,
  margin: 0,
  "& input": {
    color: "#fff",
    textAlign: "center",
  },
  "& label.Mui-focused": {
    color: "#fff",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#fff",
  },
  "& .MuiInput-underline:before": {
    border: 0,
  },
}));

const getBorderStyles = (rIndex, cIndex) => {
  let styles = {};
  const width = "2px";

  styles["borderBottom"] = "1px solid rgba(255,255,255,0.15)";
  styles["borderRight"] = "1px solid rgba(255,255,255,0.15)";

  if (rIndex % 3 === 0 && cIndex % 3 === 0) {
    styles["borderLeft"] = `${width} solid #fff`;
    styles["borderTop"] = `${width} solid #fff`;
  } else if (rIndex % 3 === 0 && cIndex % 3 === 1) {
    styles["borderTop"] = `${width} solid #fff`;
  } else if (rIndex % 3 === 0 && cIndex % 3 === 2) {
    styles["borderTop"] = `${width} solid #fff`;
    styles["borderRight"] = `${width} solid #fff`;
  } else if (rIndex % 3 === 1 && cIndex % 3 === 0) {
    styles["borderLeft"] = `${width} solid #fff`;
  } else if (rIndex % 3 === 1 && cIndex % 3 === 2) {
    styles["borderRight"] = `${width} solid #fff`;
  } else if (rIndex % 3 === 2 && cIndex % 3 === 0) {
    styles["borderLeft"] = `${width} solid #fff`;
    styles["borderBottom"] = `${width} solid #fff`;
  } else if (rIndex % 3 === 2 && cIndex % 3 === 1) {
    styles["borderBottom"] = `${width} solid #fff`;
  } else if (rIndex % 3 === 2 && cIndex % 3 === 2) {
    styles["borderBottom"] = `${width} solid #fff`;
    styles["borderRight"] = `${width} solid #fff`;
  }

  return styles;
};

class Board extends React.Component {
  state = {
    disabled: false,
    currentStep: 0,
    playing: false,
    steps: [],
    board: [...new Array(9)].map(() => [...new Array(9)].fill("")),
    invalid: false,
    solved: false,
  };

  getSolution = () => {
    if (this.state.solved) return;
    const solver = new Solver(this.state.board);
    if (solver.isValid()) {
      const solution = solver.solve();
      const steps = solver.getMemory();
      this.setState(
        { board: solution, disabled: true, steps, solved: true },
        () => {
          animate(document.getElementById("sudoku-buttons"), {
            translateY: 1,
            duration: 1000,
          });
          animate(document.getElementById("sudoku-slider"), {
            opacity: 1,
            duration: 5000,
          });
        },
      );
    } else {
      this.setState({ invalid: true });
    }
  };

  handlePlayPause = () => {
    if (this.state.playing) {
      if (this.playingInterval) {
        this.setState({ playing: false }, () =>
          clearTimeout(this.playingInterval),
        );
      }
    } else {
      this.setState({ playing: true }, () => this.play());
    }
  };

  play = () => {
    if (this.state.currentStep === this.state.steps.length - 1) {
      this.setState({ currentStep: 0 });
    }
    if (this.state.steps[this.state.currentStep + 1]) {
      this.setState(
        {
          board: this.state.steps[this.state.currentStep + 1],
          currentStep: this.state.currentStep + 1,
        },
        () => {
          this.playingInterval = setTimeout(() => this.play(), 1);
        },
      );
    } else {
      this.setState({ playing: false });
    }
  };

  clear = () => {
    if (this.playingInterval) clearTimeout(this.playingInterval);
    this.setState({ invalid: false, solved: false, currentStep: 0 }, () => {
      this.setState(
        {
          board: [...new Array(9)].map(() => [...new Array(9)].fill("")),
          disabled: false,
          playing: false,
        },
        () => {
          animate(document.getElementById("sudoku-buttons"), {
            translateY: -1,
            duration: 1000,
          });
          animate(document.getElementById("sudoku-slider"), {
            opacity: 0,
            duration: 5000,
          });
        },
      );
    });
  };

  render() {
    const isMobile = this.props.isMobile;

    const putNum = (row, col, num) => {
      if (String(num) === "" || String(num).match("[1-9]") != null) {
        let b = Object.assign([], this.state.board);
        b[row][col] = String(num);
        const sudokuBoard = new Solver(b);
        this.setState({ board: b, invalid: !sudokuBoard.isValid() });
      }
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255,255,255,0.58)",
              fontFamily: "RobotoLight",
              fontWeight: 300,
            }}
          >
            Fill valid numbers (1–9) and press <strong>Solve</strong> to watch
            the algorithm work.
          </Typography>
        </Box>

        {this.state.board.map((row, rIndex) => (
          <Box key={rIndex} style={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              {row.map((col, cIndex) => {
                const cellStyles = getBorderStyles(rIndex, cIndex);
                cellStyles["padding"] = isMobile
                  ? "5px 6px 5px 10px"
                  : "8px 15px 8px 19px";
                if (rIndex < 3 && cIndex > 2 && cIndex <= 5) {
                  cellStyles["backgroundColor"] = "rgba(255,255,255,0.06)";
                } else if (
                  rIndex >= 3 &&
                  rIndex < 6 &&
                  ((cIndex >= 0 && cIndex <= 2) || cIndex > 5)
                ) {
                  cellStyles["backgroundColor"] = "rgba(255,255,255,0.06)";
                } else if (rIndex >= 6 && cIndex > 2 && cIndex <= 5) {
                  cellStyles["backgroundColor"] = "rgba(255,255,255,0.06)";
                }
                return (
                  <Box key={cIndex} style={{ ...cellStyles }}>
                    <STextField
                      placeholder=" ."
                      value={String(this.state.board[rIndex][cIndex])}
                      inputProps={{ maxLength: "1", pattern: "[1-9]" }}
                      onChange={(e) => putNum(rIndex, cIndex, e.target.value)}
                      disabled={this.state.disabled}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}

        <Box
          style={{
            marginTop: "20px",
            display: this.state.disabled ? "block" : "none",
            width: "100%",
          }}
        >
          <Box
            id="sudoku-slider"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0,
            }}
          >
            <Box sx={{ flex: "0 0 auto", width: isMobile ? "75%" : "300px" }}>
              <Slider
                disabled={this.state.playing}
                style={{ width: "100%" }}
                step={1}
                min={0}
                value={this.state.currentStep}
                max={this.state.steps.length}
                onChange={(e, val) => {
                  if (this.playingInterval) clearTimeout(this.playingInterval);
                  if (this.state.steps[val]) {
                    this.setState({
                      playing: false,
                      board: this.state.steps[val],
                      currentStep: val,
                    });
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: "0 0 auto" }}>
              <IconButton color="primary" onClick={this.handlePlayPause}>
                {this.state.playing ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
            </Box>
          </Box>
        </Box>

        <Box style={{ marginTop: "20px", width: "100%" }} id="sudoku-buttons">
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
            }}
          >
            {this.state.invalid && (
              <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                <Alert severity="error">The board is invalid.</Alert>
              </Box>
            )}
            <Box sx={{ width: "100%", height: 0, m: 0, p: 0 }} />
            <Box sx={{ flex: "0 0 auto", width: { xs: "45%", md: "25%" } }}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                style={{ padding: "10px 0" }}
                onClick={this.clear}
              >
                Clear
              </Button>
            </Box>
            <Box sx={{ flex: "0 0 auto", width: { xs: "45%", md: "25%" } }}>
              <Button
                disabled={this.state.solved}
                variant="contained"
                color="secondary"
                fullWidth
                style={{ padding: "10px 0" }}
                onClick={this.getSolution}
              >
                Solve
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
}

export default Board;
